import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'shared/contexts/I18nContext';
import { useLoading } from 'shared/contexts/Loading.jsx';
import { useSubscription } from 'shared/contexts/Subscription';
import apiCall from 'shared/services/ApiAdapter';
import { useMasterList } from 'shared/hooks/useMasterList';
import ContentHeaderSection from '../../../shared/common/ContentHeaderSection';
import { getCurrentUserId, getUserRoleProfileId } from 'shared/utils/SessionDataUtils';

/**
 * Form for creating or updating a student's enrollment in a batch.
 * @param {object} props - The component props.
 * @param {function} props.navigateTo - Function to navigate to a different view.
 * @param {string} props.studentId - The ID of the student to enroll.
 * @param {string} props.requestedCourseId - The ID of a course requested by the student (optional).
 * @param {string} props.enrollmentId - The ID of the enrollment to update (optional).
 * @param {function} props.displayToast - Function to display toast notifications.
 */
const CreateUpdateBatchEnrollmentForm = ({ navigateTo, studentId, requestedCourseId, enrollmentId, displayToast }) => {
    const { t } = useTranslation();
    const { startLoading, stopLoading } = useLoading();
    const { canPerformAction } = useSubscription();
    const userId = getCurrentUserId();
    const storedTutorId = getUserRoleProfileId();

    console.log(enrollmentId)
    const isUpdateMode = enrollmentId ? true : false;

    // State for the enrollment, including tutorId and default values
    const [enrollment, setEnrollment] = useState({
        studentId,
        batchId: '',
        tutorId: storedTutorId,
        enrollmentDate: new Date().toISOString().split('T')[0],
        status: 'Enrolled',
    });
    const [studentDetails, setStudentDetails] = useState(null);
    const [availableBatches, setAvailableBatches] = useState([]);
    const [availableCourses, setAvailableCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Use the master list hook to fetch curricula and academic classes
    const {
        masterData: { curricula, academicClasses },
        isLoading: masterDataLoading,
        error: masterDataError
    } = useMasterList({ displayToast, startLoading, stopLoading });

    // Helper function to get the label from the master list data
    const getLabel = useCallback((value, list) => {
        const item = list.find(i => i.value === value);
        return item ? item.label : value;
    }, []);

    // Effect to fetch initial data: student details, courses, and existing enrollment (if in update mode)
    useEffect(() => {
        const fetchData = async () => {
            setFormLoading(true);
            try {
                // Fetch student and all available courses for the current tutor
                const studentData = await apiCall(`/students/${studentId}`, false, 'GET', null, startLoading, stopLoading, displayToast);
                const coursesData = await apiCall(`/courses?createdBy.equals=${userId}`, false, 'GET', null, startLoading, stopLoading, displayToast);

                setStudentDetails(studentData);
                setAvailableCourses(coursesData);

                let initialCourseId = requestedCourseId;
                let initialBatchId = '';

                // If in update mode, fetch the existing enrollment details
                if (isUpdateMode) {
                    const fetchedEnrollmentData = await apiCall(`/batch-enrollments/${enrollmentId}`, false, 'GET', null, startLoading, stopLoading, displayToast);
                    initialCourseId = fetchedEnrollmentData.course?.id;
                    initialBatchId = fetchedEnrollmentData.batch?.id;
                    setEnrollment({
                        ...fetchedEnrollmentData,
                        studentId: fetchedEnrollmentData.student?.id,
                        batchId: initialBatchId,
                        tutorId: fetchedEnrollmentData.tutor?.id || storedTutorId,
                        enrollmentDate: fetchedEnrollmentData.enrollmentDate ? fetchedEnrollmentData.enrollmentDate.split('T')[0] : new Date().toISOString().split('T')[0],
                    });
                }

                // Set the selected course ID, which will trigger the second useEffect
                setSelectedCourseId(initialCourseId);

            } catch (error) {
                console.error("Failed to fetch data for enrollment form:", error);
                displayToast(`Failed to load data: ${error.message}`, 'error');
            } finally {
                setFormLoading(false);
            }
        };
        fetchData();
    }, [studentId, requestedCourseId, enrollmentId, isUpdateMode, startLoading, stopLoading, displayToast, userId, storedTutorId]);

    // Effect to dynamically fetch batches whenever the selected course changes
    useEffect(() => {
        const fetchBatches = async () => {
            if (selectedCourseId) {
                setFormLoading(true);
                try {
                    const batchesData = await apiCall(`/batches?courseId.equals=${selectedCourseId}`, false, 'GET', null, startLoading, stopLoading, displayToast);
                    setAvailableBatches(batchesData);

                    // Auto-select the batch if there's only one or if it's the one from the enrollment in update mode
                    if (isUpdateMode && batchesData.some(b => b.id === enrollment.batchId)) {
                        setEnrollment(prev => ({ ...prev, batchId: enrollment.batchId }));
                    } else if (batchesData.length === 1) {
                        setEnrollment(prev => ({ ...prev, batchId: batchesData[0].id }));
                    } else {
                        setEnrollment(prev => ({ ...prev, batchId: '' }));
                    }

                } catch (error) {
                    console.error("Failed to fetch batches:", error);
                    displayToast(`Failed to load batches: ${error.message}`, 'error');
                } finally {
                    setFormLoading(false);
                }
            } else {
                setAvailableBatches([]);
                setEnrollment(prev => ({ ...prev, batchId: '' }));
            }
        };

        fetchBatches();
    }, [selectedCourseId, isUpdateMode, startLoading, stopLoading, displayToast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setValidationErrors(prev => ({ ...prev, [name]: '' }));
        if (name === 'courseId') {
            setSelectedCourseId(value);
        } else {
            setEnrollment(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);

        const errors = {};
        if (!selectedCourseId) errors.courseId = "Please select a course.";
        if (!enrollment.batchId) errors.batchId = "Please select a batch.";
        if (!enrollment.enrollmentDate) errors.enrollmentDate = "Please select an enrollment date.";

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setFormLoading(false);
            return;
        }

        if (!isUpdateMode && !canPerformAction('maxStudentsPerTutor', 0)) {
            displayToast(`Cannot enroll student. ${t('limitReached')}`, 'error');
            setFormLoading(false);
            return;
        }

        const payload = {
            ...enrollment,
            student: { id: enrollment.studentId },
            batch: { id: enrollment.batchId },
            course: { id: selectedCourseId },
            tutor: { id: storedTutorId },
        };

        // Handle creation-specific fields and update fields
        if (!isUpdateMode) {
            payload.isActive = true;
            payload.feePaid = 0;
            payload.paymentDate = null;
            payload.createdBy = userId;
        } else {
            payload.updatedBy = userId;
            payload.updatedDate = new Date().toISOString();
            // Remove parent field for updates to prevent foreign key errors
            delete payload.parent;
        }

        try {
            if (isUpdateMode) {
                await apiCall(`/batch-enrollments/${enrollmentId}`, false, 'PUT', payload, startLoading, stopLoading, displayToast);
                displayToast(t('enrollmentSuccessfullyUpdated'), 'success');
            } else {
                await apiCall('/batch-enrollments', false, 'POST', payload, startLoading, stopLoading, displayToast);
                displayToast(t('enrollmentSuccessfullyCreated'), 'success');
            }
            navigateTo('students');
        } catch (error) {
            console.error("Failed to save enrollment:", error.message);
            // This line was updated to pass the specific error message to the toast.
            displayToast(error.message, 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const isLoading = formLoading || masterDataLoading;

    if (masterDataError) {
        return (
            <div className="p-8 md:px-10 text-center text-red-500">
                {masterDataError}
            </div>
        );
    }

    if (isLoading && !studentDetails) {
        return <div className="p-8 md:px-10 text-center text-gray-600 dark:text-gray-400">{t('loadingEnrollmentDetails')}</div>;
    }

    const formTitle = isUpdateMode ? t('updateBatchEnrollment') : t('createBatchEnrollment');
    const submitButtonText = isUpdateMode ? t('update') : t('create');

    return (
        <div className="p-8 md:px-10">
            <ContentHeaderSection
                title={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigateTo('students')}
                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
                            aria-label="Go back to batches"
                        >
                            <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                        </button>
                        <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
                            {formTitle}
                        </span>
                    </div>
                }
                subtitle={t('enrollmentDetails')}
            />
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {studentDetails && (
                        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('student')}: {studentDetails.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Email: {studentDetails.email}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Class: {getLabel(studentDetails.academicClass, academicClasses)} | Curriculum: {getLabel(studentDetails.curriculum, curricula)}</p>
                        </div>
                    )}

                    <div>
                        <label htmlFor="courseId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Course</label>
                        <select
                            id="courseId"
                            name="courseId"
                            value={selectedCourseId}
                            onChange={handleChange}
                            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${validationErrors.courseId ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                            disabled={isUpdateMode}
                        >
                            <option value="">{t('select')}</option>
                            {availableCourses.map(course => (
                                <option key={course.id} value={course.id}>{course.courseName}</option>
                            ))}
                        </select>
                        {validationErrors.courseId && <p className="text-sm text-red-500 mt-1">{validationErrors.courseId}</p>}
                    </div>

                    <div>
                        <label htmlFor="batchId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('batch')}</label>
                        <select
                            id="batchId"
                            name="batchId"
                            value={enrollment.batchId}
                            onChange={handleChange}
                            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${validationErrors.batchId ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                            disabled={availableBatches.length === 0}
                        >
                            <option value="">{t('select')}</option>
                            {availableBatches.map(batch => (
                                <option key={batch.id} value={batch.id}>{batch.batchName}</option>
                            ))}
                        </select>
                        {selectedCourseId && availableBatches.length === 0 && (
                            <p className="text-sm text-red-500 mt-1">No batches available for this course.</p>
                        )}
                        {validationErrors.batchId && <p className="text-sm text-red-500 mt-1">{validationErrors.batchId}</p>}
                    </div>

                    <div>
                        <label htmlFor="enrollmentDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('enrollmentDate')}</label>
                        <input
                            type="date"
                            id="enrollmentDate"
                            name="enrollmentDate"
                            value={enrollment.enrollmentDate}
                            onChange={handleChange}
                            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${validationErrors.enrollmentDate ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                        />
                        {validationErrors.enrollmentDate && <p className="text-sm text-red-500 mt-1">{validationErrors.enrollmentDate}</p>}
                    </div>

                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('status')}</label>
                        <select
                            id="status"
                            name="status"
                            value={enrollment.status}
                            onChange={handleChange}
                            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${validationErrors.status ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                            disabled={!isUpdateMode}
                        >
                            <option value="Enrolled">Enrolled</option>
                            {isUpdateMode && (
                                <>
                                    <option value="Requested">Requested</option>
                                    <option value="Cancelled">Cancelled</option>
                                    <option value="Rejected">Rejected</option>
                                </>
                            )}
                        </select>
                        {validationErrors.status && <p className="text-sm text-red-500 mt-1">{validationErrors.status}</p>}
                    </div>

                    <div className="flex justify-end space-x-3">
                        <button
                            type="submit"
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            disabled={isLoading}
                        >
                            {isLoading ? (isUpdateMode ? t('updating') : t('creating')) : submitButtonText}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateUpdateBatchEnrollmentForm;
